<template>
  <div class="display" :style="{ '--settlement-height': settlementHeight + 'px' }">
    <ActiveScreen
      v-if="screen === 'active'"
      ref="activeScreenRef"
      :doc="view.doc"
      :payment="view.payment"
      :branch="view.branch"
      :rating-session="view.ratingSession"
      @rated="onInlineRated"
      @unavailable="onInlineUnavailable"
      @network-error="onNetworkError"
    />
    <RatingScreen
      v-else-if="screen === 'rating'"
      :key="view.ratingSession.id"
      ref="ratingScreenRef"
      :branch="view.branch"
      :rating-session="view.ratingSession"
      @submitted="onInteractiveRatingSubmitted"
      @expired="onRatingExpired"
      @network-error="onNetworkError"
    />
    <PostRatingScreen v-else-if="screen === 'post-rating'" :branch="postRatingBranch" @finished="onPostRatingFinished" />
    <MessageScreen v-else-if="screen === 'thank-you'" :branch="view.branch" title="ขอบคุณที่ใช้บริการ" testid="screen-thank-you" />
    <MessageScreen v-else :branch="view.branch" testid="screen-idle" />
    <!-- Shown over any screen above, for as long as a payment settlement is
         published - including after THANK YOU/RATING, so the customer can
         still check their change. Not once this display has locally moved
         itself to idle (a finished post-rating flow or a safely expired
         rating prompt): the underlying sticky view may still carry an old
         settlement, but the customer-facing flow for it is over. -->
    <SettlementFooter :settlement="effectiveSettlement" @resize="settlementHeight = $event" />
    <div v-if="fullscreenSupported && !isFullscreen" class="fullscreen-hint" data-testid="fullscreen-hint">แตะเพื่อเต็มจอ</div>
  </div>
</template>

<script>
import { createDisplayClient, createInitialDisplayState, createTransport } from '@shared/customer-display';
import { isInlineRatingAvailable } from '@shared/customer-display/protocol';
import { createHelperBridge } from '@shared/customer-display/helperBridge';
import ActiveScreen from './ActiveScreen.vue';
import MessageScreen from './MessageScreen.vue';
import PostRatingScreen from './PostRatingScreen.vue';
import RatingScreen from './RatingScreen.vue';
import SettlementFooter from './SettlementFooter.vue';
import fullscreenOnTapMixin from './fullscreenOnTapMixin';

export default {
  name: 'CustomerDisplayPage',
  components: { ActiveScreen, MessageScreen, PostRatingScreen, RatingScreen, SettlementFooter },
  mixins: [fullscreenOnTapMixin],
  data() {
    return {
      state: createInitialDisplayState(),
      settlementHeight: 0,
      // Local, display-only overrides layered on top of the published view -
      // never sent back over the protocol:
      //  - localRatedSession: set the instant RatingScreen's own submit
      //    succeeds, so the display can show the configured post-rating
      //    content even though the still-sticky published view stays
      //    RATING/'waiting' forever (nothing tells the publisher a
      //    submission happened).
      //  - forcedIdle: set once PostRatingScreen's own timer elapses, so
      //    POST_RATING -> IDLE does not need a new published message either.
      // Both reset the moment the underlying view stops being rating/
      // thank-you (the only way that happens is a genuinely new ACTIVE
      // document taking over, or an idle/release - see protocol.js's
      // sticky-view rules), so old state never leaks into the next customer
      // (Phase 4 requirement #11).
      localRatedSession: null,
      forcedIdle: false,
      // RP2040 rating controller: a browser-owned, opaque, locally-scoped
      // generation counter -- bumped every time a NEW rating opportunity
      // begins (see the ratingOpportunitySessionId watcher below). Sent on
      // every rating-state/rating-paid/rating-outcome broadcast; the Helper
      // orders/deduplicates it against its OWN per-WebSocket-connection
      // sequence number (see helperBridge.js's header and
      // RatingControllerProtocol.psm1), never against anything this page
      // persists itself -- a reload always opens a brand new connection, so
      // the Helper always recognizes it as current regardless of this
      // counter simply starting over at 0 again. Deliberately NOT
      // ratingSession.id itself -- this module (like the rest of the
      // Phase 6 bridge) never lets the Helper see real session/document
      // data, only this opaque counter. customerDisplayOpportunitySessionId
      // is purely a LOCAL, never-transmitted signal for "did the backend
      // session actually change" (see that watcher) -- a new session
      // increments the counter even when Vue never renders an intermediate
      // not-ready frame.
      customerDisplayOpportunityGeneration: 0,
      customerDisplayOpportunitySessionId: ''
    };
  },
  computed: {
    view() {
      return this.state.view;
    },
    // Unifies both paths that lead to the post-rating content: an
    // interactive submission that just succeeded on this display, or a
    // session that was already rated before payment (Phase 4 requirement
    // #9) and arrives via THANK_YOU carrying ratingSession.
    postRatingSession() {
      if (this.localRatedSession) return this.localRatedSession;
      if (this.view.kind === 'thank-you' && this.view.ratingSession) return this.view.ratingSession;
      return null;
    },
    postRatingBranch() {
      return this.view.branch;
    },
    screen() {
      if (this.forcedIdle) return 'idle';
      if (this.postRatingSession) return 'post-rating';
      if (this.view.kind === 'rating') return 'rating';
      if (this.view.kind === 'active') return 'active';
      if (this.view.kind === 'thank-you') return 'thank-you';
      return 'idle';
    },
    // Once this display has moved itself to idle on its own (forcedIdle),
    // the still-sticky published view may keep carrying an old settlement -
    // the customer-facing flow for it is over, so stop showing it too.
    effectiveSettlement() {
      return this.forcedIdle ? null : this.view.settlement;
    },
    // RP2040 rating controller: whether a customer could ACTIVELY rate right
    // now, on either screen that offers it -- NOT the same thing as
    // ActiveScreen.vue's own showInlineRating computed, which also stays
    // true for an already-rated session so its confirmed "ได้รับการ
    // ประเมินแล้ว" text keeps rendering. A rated session must never
    // advertise READY to the hardware (there is nothing left to rate), so
    // this deliberately does NOT OR in session.status === 'rated' the way
    // showInlineRating does.
    ratingOfferedNow() {
      if (this.screen === 'rating') return true;
      if (this.screen !== 'active') return false;
      return isInlineRatingAvailable(this.view.ratingSession);
    },
    ratingOpportunitySessionId() {
      return this.ratingOfferedNow && this.view.ratingSession ? this.view.ratingSession.id : '';
    }
  },
  watch: {
    'view.kind'(kind) {
      if (kind !== 'rating' && kind !== 'thank-you') {
        this.localRatedSession = null;
        this.forcedIdle = false;
      }
    },
    ratingOpportunitySessionId(sessionId) {
      // The backend evaluation-session id is used only inside this page to
      // decide whether the opportunity changed; it never crosses the local
      // Helper bridge. This is more direct than inferring a new customer from
      // a false -> true UI edge, which can be skipped when two published
      // sessions replace one another in a single render turn.
      if (sessionId && sessionId !== this.customerDisplayOpportunitySessionId) {
        this.customerDisplayOpportunitySessionId = sessionId;
        this.customerDisplayOpportunityGeneration += 1;
      }
      if (this.customerDisplayHelperBridge) {
        this.customerDisplayHelperBridge.sendState(sessionId ? 'ready' : 'unavailable', this.customerDisplayOpportunityGeneration);
      }
    },
    // Requirement: "payment completed, customer has not rated yet" -> PAID.
    // publisher.js's paymentSucceeded() is the ONLY producer of the
    // 'rating' screen in this protocol, and only ever for a still-unrated,
    // still-ratable session (an already-rated or no-longer-ratable session
    // goes to 'thank-you' instead, never 'rating') -- so this screen
    // transition alone is both a necessary and sufficient proxy for "a
    // payment just completed and rating is still owed", with no need to
    // separately inspect ratingSession.status here. Sending this more than
    // once for the same still-unrated opportunity (e.g. a HELLO replay
    // after a display refresh lands back on the same 'rating' screen) is
    // safe: the Helper's own dedup (RatingControllerProtocol.psm1's
    // PaidSentForConnectionSequence/PaidSentForGeneration, keyed on the
    // SAME (ConnectionSequence, generation) pair the Helper tracks for this
    // message) is the actual "once per opportunity" guarantee, not this
    // watcher. sendPaid itself remembers this as pending and retries it on
    // every reconnect until the Helper's own acknowledgement confirms
    // delivery (see helperBridge.js's pendingPaid) -- payment is never
    // silently lost, whether the socket was disconnected or dropped
    // mid-delivery.
    screen(next) {
      if (next === 'rating' && this.customerDisplayHelperBridge) this.customerDisplayHelperBridge.sendPaid(this.customerDisplayOpportunityGeneration);
    }
  },
  created() {
    this.customerDisplayClient = null;
    this.customerDisplayTransport = null;
    // Phase 6: an optional localhost bridge to the Windows Customer Display
    // Helper, carrying only F13/F14/F15 -> rating 1/2/3 intents from a
    // physical remote (see customer-display-helper README "Rating bridge
    // (Phase 6)"). The actual submission is the exact same handler a tap
    // uses (RatingScreen's exposed submitFromHelper, i.e. onSelect) --
    // nothing about the rating/session flow is duplicated here.
    this.customerDisplayHelperBridge = null;
    this.customerDisplayUnsubscribeHelperBridge = null;
  },
  mounted() {
    // Establish bridge correlation before starting the display client, so
    // its HELLO/replay can update `state` immediately with the opportunity
    // watcher already wired up.
    this.customerDisplayHelperBridge = createHelperBridge(window);
    this.customerDisplayUnsubscribeHelperBridge = this.customerDisplayHelperBridge.onIntent(({ requestId, rating }) => {
      this.onHelperRatingIntent(requestId, rating);
    });

    this.customerDisplayTransport = createTransport();
    this.customerDisplayClient = createDisplayClient({
      transport: this.customerDisplayTransport,
      win: window,
      onChange: (next) => {
        this.state = next;
      }
    });
    this.customerDisplayClient.start();
    // Initial READY/OFF picture for the RP2040 -- the watcher above only
    // fires on a later CHANGE, so the very first state (whatever it is the
    // moment this page mounts) needs its own explicit send here, including
    // minting the first generation if rating is already offered right at
    // mount (e.g. this page loaded directly into an active session). This
    // generation counter always starts at 0 on every page load/reload
    // (nothing persists it) -- safe because the Helper's own
    // per-connection sequence number, not this counter, is what tells a
    // reload's fresh connection apart from the one that preceded it; see
    // helperBridge.js's header and RatingControllerProtocol.psm1.
    if (this.ratingOpportunitySessionId && this.ratingOpportunitySessionId !== this.customerDisplayOpportunitySessionId) {
      this.customerDisplayOpportunitySessionId = this.ratingOpportunitySessionId;
      this.customerDisplayOpportunityGeneration += 1;
    }
    this.customerDisplayHelperBridge.sendState(this.ratingOfferedNow ? 'ready' : 'unavailable', this.customerDisplayOpportunityGeneration);

    // Component sizes are in rem; scale the root with the monitor width so
    // the layout fills a secondary display of any resolution.
    this.customerDisplayPreviousRootSize = document.documentElement.style.fontSize;
    document.documentElement.style.fontSize = 'clamp(12px, 1.15vw, 30px)';
  },
  beforeDestroy() {
    if (this.customerDisplayClient) this.customerDisplayClient.stop();
    if (this.customerDisplayTransport) this.customerDisplayTransport.close();
    if (this.customerDisplayUnsubscribeHelperBridge) this.customerDisplayUnsubscribeHelperBridge();
    if (this.customerDisplayHelperBridge) this.customerDisplayHelperBridge.close();
    document.documentElement.style.fontSize = this.customerDisplayPreviousRootSize;
  },
  methods: {
    acknowledgeRatingCompleted(sessionId, rating) {
      if (this.customerDisplayClient) this.customerDisplayClient.acknowledgeRatingCompleted(sessionId, rating);
    },
    acknowledgeFlowDone(sessionId) {
      if (this.customerDisplayClient) this.customerDisplayClient.acknowledgeFlowDone(sessionId);
    },
    acknowledgeRatingUnavailable(sessionId) {
      if (this.customerDisplayClient) this.customerDisplayClient.acknowledgeRatingUnavailable(sessionId);
    },
    // Never ACKs a rating this specific tap/press did not itself achieve.
    // ALREADY_RATED's backend-confirmed rating can legitimately differ from
    // what THIS submission attempted (a race lost to a different input --
    // e.g. a hardware press for BAD arriving just after an on-screen tap
    // for GOOD already won) -- Codex review: "ALREADY_RATED can send ACK:n
    // for a different rating than the physical button actually submitted".
    // Lighting an LED for a face this action did not achieve is misleading
    // hardware feedback, so only ACK when the confirmed rating matches what
    // was submitted; otherwise report 'expired' (maps to OFF on the RP2040
    // -- the opportunity is still over from this remote's own perspective,
    // just with nothing specific to acknowledge to it).
    sendAckForOwnSubmission(confirmedRating, submittedRating) {
      if (confirmedRating != null && confirmedRating === submittedRating) {
        this.customerDisplayHelperBridge.sendOutcome('success', confirmedRating, this.customerDisplayOpportunityGeneration);
      } else {
        this.customerDisplayHelperBridge.sendOutcome('expired', undefined, this.customerDisplayOpportunityGeneration);
      }
    },
    onInlineRated({ sessionId, rating, submittedRating }) {
      this.acknowledgeRatingCompleted(sessionId, rating);
      // ACK follows the real backend result, exactly once, from whichever
      // source (tap or hardware) actually produced it -- never merely
      // because a rating key was pressed. The sessionId identity guard
      // inside InlineRatingSection.vue's own onSelect (never emits 'rated'
      // for a request that outlived its session) is what guarantees
      // customerDisplayOpportunityGeneration, read live right here, is
      // still the SAME opportunity this result belongs to -- a genuinely
      // newer opportunity could only have started via that same component
      // observing a session change first, which is exactly what makes that
      // guard fire instead of this emit.
      this.sendAckForOwnSubmission(rating, submittedRating);
    },
    onInlineUnavailable({ sessionId }) {
      this.acknowledgeRatingUnavailable(sessionId);
      // Expired/invalid/no-longer-offered -> OFF.
      this.customerDisplayHelperBridge.sendState('unavailable', this.customerDisplayOpportunityGeneration);
    },
    onInteractiveRatingSubmitted(session, submittedRating) {
      this.localRatedSession = session;
      this.acknowledgeRatingCompleted(session.id, session.rating);
      this.sendAckForOwnSubmission(session.rating, submittedRating);
    },
    onRatingExpired() {
      // Expired, or rejected as invalid - either way rating must no longer
      // be offered for this session. Reuse the flow-done acknowledgement
      // (not just the local forcedIdle override) so the owning publisher
      // also clears its matching session/view/settlement - otherwise a
      // refresh/reopen would replay the dead RATING prompt (and its old
      // settlement) from the start (Phase 4 review finding #1).
      const sessionId = this.view.ratingSession && this.view.ratingSession.id;
      this.forcedIdle = true;
      if (sessionId) this.acknowledgeFlowDone(sessionId);
      this.customerDisplayHelperBridge.sendState('unavailable', this.customerDisplayOpportunityGeneration);
    },
    // A submit that failed for network reasons (tap or hardware alike --
    // see RatingScreen.vue's/InlineRatingSection.vue's own NETWORK_ERROR
    // branch, which emits this for EITHER caller, since submitFromHelper IS
    // onSelect) -> RETRY. The single owner of this signal: onHelperRatingIntent
    // below deliberately does NOT also inspect result.outcome for
    // 'network-error' itself, to avoid reporting the same failure twice for
    // one hardware press. Purely observational: no retry/guard/outcome
    // logic is added or changed in either of those files, only this one
    // upward signal.
    onNetworkError() {
      this.customerDisplayHelperBridge.sendOutcome('network-error', undefined, this.customerDisplayOpportunityGeneration);
    },
    onPostRatingFinished() {
      const sessionId = this.postRatingSession && this.postRatingSession.id;
      this.forcedIdle = true;
      if (sessionId) this.acknowledgeFlowDone(sessionId);
    },
    // Phase 6 hardware-remote routing. Tries the two UI states where rating
    // is actually allowed today, in preference order, and reuses each
    // one's own real submit path (RatingScreen.submitFromHelper / Active-
    // Screen.submitFromHelper -> InlineRatingSection.submitFromHelper) --
    // nothing rating/session/token/backend-related is decided or
    // duplicated here, only which already-mounted component to ask:
    //   1. the full-screen post-payment RatingScreen, if it's showing;
    //   2. the ACTIVE document screen's inline pre-payment widget, if
    //      showInlineRating has it mounted;
    //   3. otherwise 'unavailable' -- idle/thank-you/post-rating screens,
    //      or either of the above with nothing currently rateable (already
    //      submitting, already rated, expired), never get a fabricated
    //      outcome.
    async onHelperRatingIntent(requestId, rating) {
      let result = null;
      if (this.screen === 'rating' && this.$refs.ratingScreenRef) {
        result = await this.$refs.ratingScreenRef.submitFromHelper(rating);
      } else if (this.screen === 'active' && this.$refs.activeScreenRef) {
        result = await this.$refs.activeScreenRef.submitFromHelper(rating);
      }
      // result is null whenever the applicable component's own guard made
      // this a no-op (already submitting/rated/hidden, or not mounted) --
      // e.g. a tap and a remote press raced, or rating isn't allowed here.
      // (A NETWORK_ERROR result is NOT separately reported to the RP2040
      // controller here -- submitFromHelper IS onSelect, so it already
      // $emit('network-error')'d, caught by onNetworkError above; reporting
      // it again here would be a second, redundant 'rating-outcome' for the
      // same one failure.)
      this.customerDisplayHelperBridge.sendResult(requestId, result ? result.outcome : 'unavailable');
    }
  }
};
</script>

<style scoped>
/* Neutralizes this Vue 2 app's own global styling for this one route only
   (never leaks to any other page). Vue 3's build had NO such global rules
   at all (its own vue3/src/styles.css only sets a font stack + box-sizing
   reset, nothing element-specific) -- this app's src/sass/style.scss,
   loaded app-wide as a side effect of the Vuetify plugin (src/plugins/
   vuetify.js), includes `h1,h2,h3,h4,h5,h6 { font-family: 'Sarabun-Regular',
   serif !important; }` among other rules, which otherwise overrides every
   heading here regardless of this component's own scoped CSS (an
   !important element-selector rule beats a scoped rule that never sets
   font-family at all). ::v-deep reaches into the child components
   (ActiveScreen/RatingScreen/etc.) that actually render the affected
   elements; the more specific, !important selector here wins over the
   global one.) */
.display,
.display ::v-deep * {
  box-sizing: border-box;
  font-family: inherit !important;
}
.display {
  /* One knob for every font size on the customer display (1 = the base sizes). */
  --font-scale: 1.1;
  height: 100vh;
  background: #fff;
  cursor: default;
  user-select: none;
  /* Matches vue3/src/styles.css's body font stack exactly, so this route
     renders with the same typeface Vue 3 used regardless of whatever font
     the rest of this Vue 2 app sets elsewhere. */
  font-family: 'Kanit', 'Sarabun', 'Noto Sans Thai', system-ui, -apple-system, 'Segoe UI', sans-serif !important;
  color: #111827;
  -webkit-font-smoothing: antialiased;
}
.fullscreen-hint {
  position: fixed;
  left: 16px;
  bottom: 12px;
  font-size: calc(15px * var(--font-scale));
  color: #6b7280;
  opacity: 0.8;
  pointer-events: none;
}
</style>
